<template>
<div class="outer_box">
  <div class="all_out_box">
    <div class="tttiltleBox">
      <div class="title-container">
        <h1 class="main-title main-title-edit">
          <span class="icon-title">🏠</span>
          <span class="house-name-text">{{ editPageBuildingName }}</span>
          <template v-if="editPageRoomLabel">
            <span class="title-room-sep" aria-hidden="true">·</span>
            <span class="house-room-text">{{ editPageRoomLabel }}</span>
          </template>
        </h1>
        <div class="title-underline"></div>
        <p class="subtitle">Edit your review</p>
        <p v-if="isDraftModeration" class="draft-edit-hint">
          This review is saved as a <strong>draft</strong> — use <strong>Save draft</strong> to keep working, or <strong>Publish review</strong> when ready to submit.
        </p>
        <p v-if="isDraftModeration" class="edit-review-giveaway-nudge">
          Campaign checklist (photos, comments) lives on your
          <router-link to="/userProfile" class="edit-review-inline-link">Profile</router-link>.
        </p>
        <div class="listing-room-banner listing-room-banner--compact" v-if="houseId">
          <div class="listing-room-inner">
            <div class="listing-room-text">
              <span class="listing-room-label">Unit for this review</span>
              <strong class="listing-room-name">{{ editPageRoomLabel || ('Room #' + houseLetId) }}</strong>
              <p class="listing-room-hint listing-room-hint--inline">Switch if this write-up should apply to another unit in the same building.</p>
            </div>
            <button type="button" class="btn-switch-room" @click="goAddOrSwitchRoom">
              Add or switch room
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="top_rate">
      <div class="top_rate_right">
        <div class="form-section">
          <h3 class="section-title"><span class="section-icon">🏠</span> Stay Context</h3>
          
          <div class="input-group">
            <div class="input-field">
              <label class="form-label">Room Type</label>
              <div class="select-container">
                <select v-model="roomType" class="custom-input modern-input">
                  <option value="" disabled>Select room type</option>
                  <option value="Private">Private</option>
                  <option value="Shared">Shared</option>
                  <option value="Entire Unit">Entire Unit</option>
                </select>
              </div>
            </div>

            <div class="input-field">
              <label class="form-label">Stay Duration</label>
              <div class="select-container">
                <select v-model="stayDuration" class="custom-input modern-input">
                  <option value="" disabled>Select duration</option>
                  <option value="1">&lt;3 months</option>
                  <option value="2">3–6 months</option>
                  <option value="3">6–12 months</option>
                  <option value="4">1+ year</option>
                </select>
              </div>
            </div>

            <div class="input-field">
              <label class="form-label">Number of Roommates</label>
              <div class="input-container">
                <input
                  v-model="roommatesNum"
                  type="number"
                  min="0"
                  placeholder="Enter number"
                  class="custom-input modern-input"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-title"><span class="section-icon">💰</span> Pricing Details</h3>
          
          <div class="input-group">
            <div class="input-field">
              <label class="form-label">Monthly Rent</label>
              <div class="input-container">
                <span class="input-prefix">HKD</span>
                <input
                  v-model="monthlyRent"
                  type="number"
                  min="0"
                  placeholder="e.g. 5000"
                  class="custom-input modern-input with-prefix"
                />
              </div>
            </div>

            <div class="input-field">
              <label class="form-label">Deposit</label>
              <div class="input-container">
                <span class="input-prefix">HKD</span>
                <input
                  v-model="deposit"
                  type="number"
                  min="0"
                  placeholder="e.g. 10000"
                  class="custom-input modern-input with-prefix"
                />
              </div>
            </div>

            <div class="input-field">
              <label class="form-label">Bills Included?</label>
              <div class="toggle-group">
                <button type="button" :class="{'toggle-btn': true, 'active': billsIncluded === true}" @click="billsIncluded = true">Yes</button>
                <button type="button" :class="{'toggle-btn': true, 'active': billsIncluded === false}" @click="billsIncluded = false">No</button>
              </div>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-title"><span class="section-icon">🛏️</span> Room Setup</h3>
          
          <div class="input-group">
            <div class="input-field">
              <label class="form-label">Shared Bathroom?</label>
              <div class="toggle-group">
                <button type="button" :class="{'toggle-btn': true, 'active': isShared === true}" @click="isShared = true">Yes</button>
                <button type="button" :class="{'toggle-btn': true, 'active': isShared === false}" @click="isShared = false">No</button>
              </div>
            </div>

            <div class="input-field">
              <label class="form-label">Bed Type</label>
              <div class="button-container">
                <button
                  v-for="(item, index) in buttons"
                  :key="index"
                  type="button"
                  :class="{'active': selectedButton === item, 'modern-btn': true}"
                  @click="selectButton(item)"
                  class="bed-type-button"
                >
                  <span class="btn-icon">🛏️</span> {{ item }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="tmp_rate">
      <div class="rateBlock">
        <h3 class="section-title"><span class="section-icon">⭐</span> Rate Your Experience</h3>
        
        <div class="rating-grid">
          <div class="rating-item">
            <div class="rating-header">
              <div class="icon-container">
                <svg t="1741248366345" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2664" width="24" height="24">
                  <path d="M945.152 174.592c0-19.456-15.872-35.328-35.328-35.84L870.4 138.24c-2.56 0-83.968-2.56-166.912-36.352-87.552-35.84-144.384-76.288-146.432-77.312l-23.552-17.408c-12.8-9.216-29.696-9.216-41.984 0l-23.552 16.896c-0.512 0.512-58.368 41.984-146.432 77.824C238.592 135.68 156.672 138.24 154.624 138.24h-40.448c-19.456 0.512-35.328 16.384-35.328 35.84v400.384c0 100.352 57.344 212.48 157.696 308.224C327.68 969.728 433.152 1024 512 1024c78.848 0 184.32-54.272 275.456-141.312 100.352-95.744 157.696-207.872 157.696-308.224V174.592z m-207.36 656.384C662.016 903.168 570.88 952.32 512 952.32s-150.016-48.64-225.792-121.344c-86.016-81.92-135.168-175.616-135.168-256V209.92h4.096c8.192 0 99.328-3.584 193.024-41.472C443.904 129.024 506.88 83.968 509.44 81.92l2.56-2.048 2.56 2.048c7.68 5.632 68.608 48.128 161.792 86.016 93.696 37.888 184.832 41.472 193.024 41.472h4.096v365.056c0 80.896-49.664 174.08-135.68 256.512z" fill="#3bc2ff" p-id="2665"></path>
                  <path d="M669.184 352.256l-204.8 204.8L377.856 471.04c-17.92-17.92-46.592-17.92-64.512 0s-17.92 46.592 0 64.512l118.272 118.272c8.704 8.704 19.968 13.312 32.256 13.312s23.552-4.608 32.256-13.312l237.056-237.056c17.92-17.92 17.92-46.592 0-64.512-17.408-17.92-46.08-17.92-64 0z" fill="#3bc2ff" p-id="2666"></path>
                </svg>
              </div>
              <span class="rating-label">Safety</span>
            </div>
            <div class="rating-hint">How safe did you feel living in this property and surrounding area?</div>
            <el-rate
              v-model="safety"
              :colors="['#99A9BF', '#F7BA2A', '#FF9900']"
              class="el_rate_yes"
            />
          </div>

          <div class="rating-item">
            <div class="rating-header">
              <div class="icon-container">
                <svg t="1741248572206" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="3867" width="24" height="24">
                  <path d="M854.6 289.1c-18.8-43.4-45.7-82.3-79.9-115.7-34.2-33.4-73.9-59.5-118.2-77.8C610.7 76.6 562.1 67 512 67c-50.1 0-98.7 9.6-144.5 28.5-44.3 18.3-84 44.5-118.2 77.8-34.2 33.4-61.1 72.4-79.9 115.7-19.5 45-29.4 92.8-29.4 142 0 70.6 16.9 140.9 50.1 208.7 26.7 54.5 64 107.6 111 158.1 80.3 86.2 164.5 138.9 188.4 153 6.9 4.1 14.7 6.1 22.4 6.1 7.8 0 15.5-2 22.4-6.1 23.9-14.1 108.1-66.8 188.4-153 47-50.4 84.3-103.6 111-158.1C867.1 572 884 501.8 884 431.1c0-49.2-9.9-97-29.4-142zM512 880.2c-65.9-41.9-300-207.8-300-449.1 0-77.9 31.1-151.1 87.6-206.3C356.3 169.5 431.7 139 512 139s155.7 30.5 212.4 85.9C780.9 280 812 353.2 812 431.1c0 241.3-234.1 407.2-300 449.1z" p-id="3868" fill="#3bc2ff"></path>
                  <path d="M512 263c-97.2 0-176 78.8-176 176s78.8 176 176 176 176-78.8 176-176-78.8-176-176-176z m79.2 255.2C570 539.3 541.9 551 512 551c-29.9 0-58-11.7-79.2-32.8C411.7 497 400 468.9 400 439c0-29.9 11.7-58 32.8-79.2C454 338.6 482.1 327 512 327c29.9 0 58 11.6 79.2 32.8C612.4 381 624 409.1 624 439c0 29.9-11.6 58-32.8 79.2z" p-id="3869" fill="#3bc2ff"></path>
                </svg>
              </div>
              <span class="rating-label">Location Convenience</span>
            </div>
            <div class="rating-hint">How convenient is this location for daily student life (campus, transport, food)?</div>
            <el-rate
              v-model="location"
              :colors="['#99A9BF', '#F7BA2A', '#FF9900']"
              class="el_rate_yes"
            />
          </div>

          <div class="rating-item">
            <div class="rating-header">
              <div class="icon-container">
                <svg x="1741248732697" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="13377" width="24" height="24">
                  <path d="M580 888h296a20 20 0 0 1 20 20 20 20 0 0 1-20 20h-296a20 20 0 0 1-20-20 20 20 0 0 1 20-20zM493.2 877.28l15.52-58a20 20 0 0 1 24.48-14.08 20 20 0 0 1 14.16 24.48L531.84 888a20 20 0 0 1-24.56 14.16 20 20 0 0 1-14.08-24.88zM341.2 877.28l15.52-58a20 20 0 0 1 24.48-14.08 20 20 0 0 1 14.16 24.48L379.84 888a20 20 0 0 1-24.56 14.16 20 20 0 0 1-14.08-24.88zM417.2 877.28l15.52-58a20 20 0 0 1 24.48-14.08 20 20 0 0 1 14.16 24.48L456 888a20 20 0 0 1-24.56 14.16 20 20 0 0 1-14.24-24.88z" fill="#3bc2ff" p-id="13378"></path>
                  <path d="M569.2 877.28l15.52-58a20 20 0 0 1 24.48-14.08 20 20 0 0 1 14.16 24.48L608 888a20 20 0 0 1-24.56 14.16 20 20 0 0 1-14.24-24.88z" fill="#3bc2ff" p-id="13379"></path>
                  <path d="M684.32 849.52L655.36 760H324.72l-39.36 92.24a25.84 25.84 0 0 0-1.92 9.44 24 24 0 0 0 24 24h397.28a104 104 0 0 1-20.4-36.16zM298.32 720h386.08l38 117.2a64 64 0 0 0 20.88 30.32l37.12 29.68a16 16 0 0 1-10 28.48H307.44a64 64 0 0 1-64-64 63.36 63.36 0 0 1 5.2-25.12z" fill="#3bc2ff" p-id="13380"></path>
                  <path d="M228 720h512a20 20 0 0 1 20 20 20 20 0 0 1-20 20h-512a20 20 0 0 1-20-20 20 20 0 0 1 20-20z" fill="#3bc2ff" p-id="13381"></path>
                  <path d="M376 720h216v-16a40 40 0 0 0-40-40H416a40 40 0 0 0-40 40z m40-96h136a80 80 0 0 1 80 80v56H336v-56a80 80 0 0 1 80-80z" fill="#3bc2ff" p-id="13382"></path>
                  <path d="M526.64 624l84.32-460.48a24.4 24.4 0 0 0 0-4.16A23.36 23.36 0 0 0 588 136h-9.28a40 40 0 0 0-39.36 32.8L456 624z m52.08-528h9.28a63.44 63.44 0 0 1 63.36 63.36 60 60 0 0 1-1.04 11.36L560 664H408l92-502.4a80 80 0 0 1 78.72-65.6z" fill="#3bc2ff" p-id="13383"></path>
                  <path d="M250.48 397.04l8 20.56a32 32 0 0 0 17.76 17.76l20.56 8a8 8 0 0 1 4.48 10.4 8 8 0 0 1-4.48 4.48l-20.56 8a32 32 0 0 0-17.76 17.76l-8 20.56a8 8 0 0 1-14.88 0l-8-20.56a32 32 0 0 0-17.76-17.76l-20.56-8a8 8 0 0 1-4.48-10.4 8 8 0 0 1 4.48-4.48l20.56-8a32 32 0 0 0 17.76-17.76l8-20.56a8 8 0 0 1 10.4-4.48 8 8 0 0 1 4.48 4.48zM837.6 597.04l9.12 22.8a32 32 0 0 0 17.84 17.76l22.8 9.12a8 8 0 0 1 4.4 10.4 8 8 0 0 1-4.4 4.48l-22.8 9.12a32 32 0 0 0-17.84 17.84l-9.12 22.8a8 8 0 0 1-10.4 4.4 8 8 0 0 1-4.48-4.4l-9.12-22.8a32 32 0 0 0-17.76-17.84l-22.8-9.12a8 8 0 0 1-4.48-10.4 8 8 0 0 1 4.48-4.48l22.8-9.12a32 32 0 0 0 17.76-17.76l9.12-22.8a8 8 0 0 1 10.4-4.48 8 8 0 0 1 4.48 4.48z" fill="#3bc2ff" p-id="13384"></path>
                </svg>
              </div>
              <span class="rating-label">Cleanliness & Condition</span>
            </div>
            <div class="rating-hint">How clean and well-maintained was the property?</div>
            <el-rate
              v-model="cleanliness"
              :colors="['#99A9BF', '#F7BA2A', '#FF9900']"
              class="el_rate_yes"
            />
          </div>

          <div class="rating-item">
            <div class="rating-header">
              <div class="icon-container">
                <svg t="1741248869926" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="24003" width="24" height="24">
                  <path d="M409.6 336.756364l4.887273 47.941818 76.8 76.567273-33.978182-329.076364a34.210909 34.210909 0 0 0-67.258182-4.654546l-46.545454 186.181819L401.221818 372.363636zM274.850909 601.134545H64a34.210909 34.210909 0 0 0 0 68.421819H302.545455a34.210909 34.210909 0 0 0 33.28-26.065455l23.272727-101.003636L302.545455 485.934545zM933.469091 560.872727A60.509091 60.509091 0 0 0 884.363636 587.403636h-162.909091l-30.254545-166.865454a34.210909 34.210909 0 0 0-65.861818-5.585455l-46.545455 134.050909 53.76 53.76 13.498182-39.098181 10.705455 63.301818 27.927272 27.927272a43.52 43.52 0 0 0 5.818182 0H884.363636a60.741818 60.741818 0 1 0 50.036364-94.952727zM518.283636 723.316364L515.723636 698.181818l-76.8-76.8L465.454545 884.363636a34.210909 34.210909 0 0 0 30.021819 30.487273h4.189091a33.978182 33.978182 0 0 0 32.116363-23.272727l45.381818-130.792727-53.76-53.76zM174.545455 202.705455a34.210909 34.210909 0 0 0-48.407273 48.407272l661.410909 661.410909a34.210909 34.210909 0 0 0 23.272727 10.007273 34.210909 34.210909 0 0 0 23.272727-58.414545z" fill="#3bc2ff" p-id="24004"></path>
                </svg>
              </div>
              <span class="rating-label">Noise Exposure</span>
            </div>
            <div class="rating-hint">How disruptive was noise during your stay?</div>
            <el-rate
              v-model="noiseLevel"
              :colors="['#99A9BF', '#F7BA2A', '#FF9900']"
              class="el_rate_yes"
            />
          </div>

          <div class="rating-item">
            <div class="rating-header">
              <div class="icon-container">
                <svg t="1741248911883" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="26234" width="24" height="24">
                  <path d="M168 88l-18.016 16.992-44.992 44.992-16.992 18.016 12.992 20.992 67.008 112 8.992 16H271.04l128 127.008c-114.336 114.912-259.84 260.8-264.992 266.016-50.144 50.112-50.304 132.288 0.96 180 50.08 49.376 131.456 50.624 179.04 0l0.96-1.024L512 691.008l198.016 198.976 2.976 2.016c50.208 47.392 130.112 47.936 177.024-2.016v-0.96h0.96c49.024-50.112 49.536-131.584-0.96-179.04l-1.024-0.96-166.976-166.016c112.32-10.464 200.32-105.376 201.984-220h0.992c0.064-0.672-0.064-1.344 0-2.016V320c3.104-37.024-4.832-72.32-24-103.008L879.04 183.04l-28 28-121.024 120.992-44.992-47.008 118.016-117.984 33.984-34.016-44-17.984A224.096 224.096 0 0 0 704 96c-123.04 0-224 100.96-224 224 0 13.408 2.848 25.056 4.992 36.992-14.016 14.08-24.544 25.504-40.992 42.016L316.992 272V176.992l-16-8.992-112-67.008-20.992-12.992zM704 160c4.544 0 7.68 2.624 12 3.008l-99.008 98.976-21.984 22.016 21.984 23.008 90.016 92.992 21.984 23.008 23.008-23.008 104-104c0.672 7.36 5.824 12.736 4.992 20.992V320c0 88.16-71.84 160-160 160-11.712 0-25.984-2.816-41.984-6.016l-17.024-2.976-12 12-360.96 361.984h-1.024v1.024c-22.784 24.864-62.528 25.44-88.992-1.024v-0.992h-1.024c-24.864-22.784-25.44-62.528 1.024-88.992 12.096-12.128 247.008-249.024 361.984-364l13.024-12.992-5.024-18.016A198.08 198.08 0 0 1 544 320c0-88.16 71.84-160 160-160z m-526.016 8l75.008 46.016v32l-6.976 6.976h-32L168 178.016l10.016-10.016z m468 388.992l199.04 198.016v0.992h0.96c24.896 22.784 25.472 62.528-0.96 88.992h-1.024v1.024c-22.784 24.864-62.528 25.44-88.992-1.024l-198.016-198.976 89.024-89.024z" p-id="26235" fill="#3bc2ff"></path>
                </svg>
              </div>
              <span class="rating-label">Maintenance Responsiveness</span>
            </div>
            <div class="rating-hint">How quickly and effectively were issues fixed?</div>
            <el-rate
              v-model="maintenance"
              :colors="['#99A9BF', '#F7BA2A', '#FF9900']"
              class="el_rate_yes"
            />
          </div>

          <div class="rating-item">
            <div class="rating-header">
              <div class="icon-container">
                <svg t="1741248656029" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="6856" width="24" height="24">
                  <path d="M567.424 56a32 32 0 0 1 18.944 33.28l-38.144 305.408H896a32 32 0 0 1 24.576 52.48l-426.688 512a32 32 0 0 1-56.32-24.448l38.144-305.344H128a32 32 0 0 1-24.576-52.48l426.624-512a32 32 0 0 1 37.376-8.96z m-371.2 509.376H512a32 32 0 0 1 31.744 35.968l-29.12 232.96 313.024-375.68H512a32 32 0 0 1-31.744-35.904l29.056-233.024-313.088 375.68z" p-id="6857" fill="#3bc2ff"></path>
                </svg>
              </div>
              <span class="rating-label">Listing Accuracy</span>
            </div>
            <div class="rating-hint">Did the property match the listing photos and description?</div>
            <el-rate
              v-model="utilities"
              :colors="['#99A9BF', '#F7BA2A', '#FF9900']"
              class="el_rate_yes"
            />
          </div>


          
          <div class="rating-item">
            <div class="rating-header">
              <div class="icon-container">
                <svg t="1741248656029" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="6856" width="24" height="24">
                  <path d="M567.424 56a32 32 0 0 1 18.944 33.28l-38.144 305.408H896a32 32 0 0 1 24.576 52.48l-426.688 512a32 32 0 0 1-56.32-24.448l38.144-305.344H128a32 32 0 0 1-24.576-52.48l426.624-512a32 32 0 0 1 37.376-8.96z m-371.2 509.376H512a32 32 0 0 1 31.744 35.968l-29.12 232.96 313.024-375.68H512a32 32 0 0 1-31.744-35.904l29.056-233.024-313.088 375.68z" p-id="6857" fill="#3bc2ff"></path>
                </svg>
              </div>
              <span class="rating-label">Internet & Utilities Reliability</span>
            </div>
            <div class="rating-hint">How reliable were internet, water, and electricity?</div>
            <el-rate
              v-model="internet"
              :colors="['#99A9BF', '#F7BA2A', '#FF9900']"
              class="el_rate_yes"
            />
          </div>


          
          <div class="rating-item">
            <div class="rating-header">
              <div class="icon-container">
                <svg t="1741248656029" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="6856" width="24" height="24">
                  <path d="M567.424 56a32 32 0 0 1 18.944 33.28l-38.144 305.408H896a32 32 0 0 1 24.576 52.48l-426.688 512a32 32 0 0 1-56.32-24.448l38.144-305.344H128a32 32 0 0 1-24.576-52.48l426.624-512a32 32 0 0 1 37.376-8.96z m-371.2 509.376H512a32 32 0 0 1 31.744 35.968l-29.12 232.96 313.024-375.68H512a32 32 0 0 1-31.744-35.904l29.056-233.024-313.088 375.68z" p-id="6857" fill="#3bc2ff"></path>
                </svg>
              </div>
              <span class="rating-label">Cost Transparency</span>
            </div>
            <div class="rating-hint">Were all costs clearly explained without hidden fees?</div>
            <el-rate
              v-model="rentValue"
              :colors="['#99A9BF', '#F7BA2A', '#FF9900']"
              class="el_rate_yes"
            />
          </div>


          
          <div class="rating-item">
            <div class="rating-header">
              <div class="icon-container">
                <svg t="1741248656029" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="6856" width="24" height="24">
                  <path d="M567.424 56a32 32 0 0 1 18.944 33.28l-38.144 305.408H896a32 32 0 0 1 24.576 52.48l-426.688 512a32 32 0 0 1-56.32-24.448l38.144-305.344H128a32 32 0 0 1-24.576-52.48l426.624-512a32 32 0 0 1 37.376-8.96z m-371.2 509.376H512a32 32 0 0 1 31.744 35.968l-29.12 232.96 313.024-375.68H512a32 32 0 0 1-31.744-35.904l29.056-233.024-313.088 375.68z" p-id="6857" fill="#3bc2ff"></path>
                </svg>
              </div>
              <span class="rating-label">Space Adequacy</span>
            </div>
            <div class="rating-hint">Was the room size suitable for the number of occupants?</div>
            <el-rate
              v-model="roomSize"
              :colors="['#99A9BF', '#F7BA2A', '#FF9900']"
              class="el_rate_yes"
            />
          </div>


          
          <div class="rating-item">
            <div class="rating-header">
              <div class="icon-container">
                <svg t="1741248656029" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="6856" width="24" height="24">
                  <path d="M567.424 56a32 32 0 0 1 18.944 33.28l-38.144 305.408H896a32 32 0 0 1 24.576 52.48l-426.688 512a32 32 0 0 1-56.32-24.448l38.144-305.344H128a32 32 0 0 1-24.576-52.48l426.624-512a32 32 0 0 1 37.376-8.96z m-371.2 509.376H512a32 32 0 0 1 31.744 35.968l-29.12 232.96 313.024-375.68H512a32 32 0 0 1-31.744-35.904l29.056-233.024-313.088 375.68z" p-id="6857" fill="#3bc2ff"></path>
                </svg>
              </div>
              <span class="rating-label">Living Comfort</span>
            </div>
            <div class="rating-hint">How comfortable was the overall living experience (privacy, shared spaces)?</div>
            <el-rate
              v-model="roomMates"
              :colors="['#99A9BF', '#F7BA2A', '#FF9900']"
              class="el_rate_yes"
            />
          </div>

        </div>
      </div>
    </div>

    <div class="reviewBlock">
      <div class="newb">
        <h2 class="review-title"><span class="review-icon">📝</span> Write a Review</h2>
        <p class="review-description">Tell us about your experience with this student housing. What's working well? What could be improved?</p>
        
<div v-if="filea!=''">
  <div class="image-gallery">
    <div class="image-preview" v-for="(image, index) in getimages(filea)" :key="index">
      <img :src="image" alt="Review image" class="review-image" />
      <button @click="deleteImage(index)" class="delete-button">x</button>
    </div>
  </div>
</div>
        <div class="upload-section">
          <h3 class="upload-title"><span class="upload-icon">📷</span> Upload photos of your real experience in this property</h3>
          <div class="upload-container">
            <div class="file-input-wrapper">
              <input type="file" @change="handleFileUpload2" placeholder="Choose File" class="file-input" accept="image/*" />
              <div class="file-input-overlay">
                <span class="upload-icon">📷</span>
                <span class="upload-text">Choose Images</span>
              </div>
            </div>

            <div class="image-gallery" v-if="images.length > 0">
              <div
                v-for="(image, index) in images"
                :key="index"
                class="image-preview"
              >
                <img :src="image" alt="Uploaded Image" />
                <button @click="removeImage2(index)" class="delete-button">
                  <span>x</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="review-textarea-container">
          <textarea 
            v-model="reviewText" 
            placeholder="What do you want other students to know about this housing?" 
            maxlength="350"
            class="review-textarea"
          ></textarea>
          <div class="character-count">{{ reviewText.length }}/350</div>
        </div>

        <!-- Insight Fields -->
        <div class="insight-section">
          <h3 class="section-title"><span class="section-icon">💡</span> Quick Insights</h3>
          <div class="input-group">
            <div class="input-field" style="flex: 1 1 100%;">
              <label class="form-label">What was the biggest problem?</label>
              <textarea v-model="biggestProblem" placeholder="e.g. Mould in bathroom, noisy neighbours, slow maintenance..." maxlength="200" class="review-textarea" style="min-height: 60px; height: 60px;"></textarea>
              <div class="character-count">{{ (biggestProblem || '').length }}/200</div>
            </div>
          </div>
          <div class="input-group">
            <div class="input-field" style="flex: 1 1 100%;">
              <label class="form-label">Would you recommend this place?</label>
              <div class="toggle-group">
                <button type="button" :class="{'toggle-btn': true, 'active': recommendation === true}" @click="recommendation = true">👍 Yes</button>
                <button type="button" :class="{'toggle-btn': true, 'active': recommendation === false}" @click="recommendation = false">👎 No</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Tag Selection -->
        <div class="insight-section">
          <h3 class="section-title"><span class="section-icon">🏷️</span> Tags (select all that apply)</h3>
          <div class="tag-grid">
            <button type="button" v-for="tag in availableTags" :key="tag" :class="{'tag-chip': true, 'active': selectedTags.includes(tag)}" @click="toggleTag(tag)">{{ tag }}</button>
          </div>
        </div>


        <div v-if="isDraftModeration" class="review-actions-row edit-draft-actions">
          <button type="button" class="draft-save-edit-button" @click="saveDraftEdits">
            Save draft
          </button>
          <button type="button" class="submit-button edit-publish-button" @click="publishDraftReview">
            <span class="button-text">Publish review</span>
            <span class="button-icon">→</span>
          </button>
        </div>
        <button v-else class="submit-button" @click="submitReview">
          <span class="button-text">Save changes</span>
          <span class="button-icon">→</span>
        </button>
        
        <p class="agreement-text">
          <template v-if="isDraftModeration">
            Saving a draft does not publish your review. Publishing sends it through the same moderation path as submitting a new review.
          </template>
          <template v-else>
          By clicking the "Save changes" button, I confirm that I have read and agree to Sparrow's
          <a href="#/terms-of-use#appendix-b" target="_blank" rel="noopener" class="agreement-link">Community Guidelines</a>,
          <a href="#/terms-of-use" target="_blank" rel="noopener" class="agreement-link">Terms of Use</a>, and
          <a href="#/privacy-policy" target="_blank" rel="noopener" class="agreement-link">Privacy Policy</a>.
          Submitted reviews may be used to enhance the student housing experience.
          </template>
        </p>
      </div>
    </div>
  </div>
</div>
</template>
<style>
.listing-room-banner {
  margin-top: 1rem;
  padding: 1rem 1.15rem;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #bae6fd;
  border-radius: 14px;
  text-align: left;
}
.listing-room-banner--compact {
  margin-top: 0.9rem;
}
.listing-room-banner--compact .listing-room-inner {
  align-items: flex-start;
}
.listing-room-hint--inline {
  margin: 0.35rem 0 0;
  font-size: 0.8rem;
  line-height: 1.45;
  max-width: 420px;
}
.listing-room-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.listing-room-label {
  display: block;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  font-weight: 600;
}
.listing-room-name {
  font-size: 1.05rem;
  color: #0f172a;
}
.btn-switch-room {
  flex-shrink: 0;
  padding: 10px 18px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #3bc2ff, #00c5c5);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.9rem;
  box-shadow: 0 4px 14px rgba(59, 194, 255, 0.35);
}
.btn-switch-room:hover {
  filter: brightness(1.05);
}
.listing-room-hint {
  margin: 0.65rem 0 0;
  font-size: 0.82rem;
  color: #64748b;
  line-height: 1.45;
}

.draft-edit-hint {
  margin: 0.75rem auto 0;
  max-width: 520px;
  font-size: 0.92rem;
  color: #475569;
  line-height: 1.5;
  text-align: center;
}

.edit-review-giveaway-nudge {
  margin: 0.5rem auto 0;
  max-width: 520px;
  font-size: 0.87rem;
  color: #64748b;
  line-height: 1.5;
  text-align: center;
}
.edit-review-inline-link {
  color: #0284c7;
  font-weight: 600;
  text-decoration: underline;
}

.review-actions-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 28px;
  align-items: stretch;
}
.edit-draft-actions .draft-save-edit-button,
.edit-draft-actions .submit-button {
  flex: 1 1 160px;
  margin-top: 0;
}
.draft-save-edit-button {
  padding: 14px 0;
  border-radius: 14px;
  border: 2px solid #b3d1f7;
  background: #f8fbff;
  color: #357ab8;
  font-weight: 700;
  font-size: 1.02rem;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}
.draft-save-edit-button:hover {
  background: #e6f0ff;
  border-color: #4a90e2;
}
.edit-publish-button {
  margin-top: 0;
}

.image-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
}

.image-item {
  position: relative;
  width: 200px;
  height: 200px;
}

.review-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
}

.delete-button {
  position: absolute;
  top: 8px;  /* 控制按钮的位置 */
  right: 8px;  /* 控制按钮的位置 */
  background-color: #e74c3c;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  z-index: 10;  /* 确保按钮位于图片的上方 */
}

.delete-button:hover {
  background-color: #c0392b;
}

.final-result {
  margin-top: 32px;
  text-align: center;
}

pre {
  background-color: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  font-size: 1rem;
  color: #333;
}

</style>
<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import imageCompression from 'browser-image-compression';
export default{
  data()  {
return{
  iconClasses: ['icon-rate-face-1', 'icon-rate-face-2', 'icon-rate-face-3'],
  colors: ['#99A9BF', '#F7BA2A', '#FF9900'],  // equivalent to { 2: '#99A9BF', 4: { value: '#F7BA2A', excluded: true }, 5: '#FF9900' }
  houseId:'',
  houseLetId:'',
  images: [],
  fileschos: [], // stores uploaded file objects
  isCompressingImage: false,
  buttons: ['Bunk', '1 Bed', '2+ Beds'],
  selectedButton: null, // 初始化时没有按钮被选中
  file: null,
  reviewId:0,
      fileName: '',
      fileContent: '',
        safety: 0,
        location: 0,
        cleanliness: 0,
        noiseLevel: 0,
        maintenance: 0,
        utilities: 0,
        internet: 0,
        rentValue: 0,
        roomMates: 0,
        roommatesNum: 0,
  roomSize:0,
        reviewText: '',
      imageUploadId: 'image-upload',
      house:{},
      houselet:{},
      filesUrl: '',
  roomType: '',
  stayDuration: '',
  monthlyRent: null,
  deposit: null,
  billsIncluded: null,
  biggestProblem: '',
  recommendation: null,
  selectedTags: [],
  availableTags: [
    'Nearby university', 'Far from university', 'Clean', 'Dirty',
    'Quiet', 'Noisy', 'Safe', 'Unsafe',
    'Spacious', 'Cramped', 'Worth the price', 'Not worth the price',
    'Accurate listing', 'Misleading photos', 'Transparent pricing', 'Hidden fees',
    'Well maintained', 'Poor maintenance', 'Convenient location', 'Inconvenient location'
  ],
  isShared: null,
  company: '',
  /** From GET getDetailedReview — building label on the rating row (fallback if /api/house is slow or fails). */
  ratingHouseName: '',
  filea:'',
  filelista:[],
  moderationStatus: '',
  editSaveIntent: null



};
  },
  computed: {
    isDraftModeration() {
      return String(this.moderationStatus || '').toUpperCase() === 'DRAFT';
    },
    editPageBuildingName() {
      const h = this.house && typeof this.house === 'object' ? this.house : {};
      const n = (h.name && String(h.name).trim()) || (this.ratingHouseName && String(this.ratingHouseName).trim()) || '';
      if (n) return n;
      if (this.houseId) return `Listing (house #${this.houseId})`;
      return 'Loading listing…';
    },
    editPageRoomLabel() {
      const hl = this.houselet && typeof this.houselet === 'object' ? this.houselet : {};
      const n = (hl.houseLetName || hl.houseletname || '').trim();
      if (n) return n;
      if (this.houseLetId != null && this.houseLetId !== '') return `Room #${this.houseLetId}`;
      return '';
    }
  },
  mounted(){
    const reviewId = this.$route.query.id;
    const presetHouseLetId = this.$route.query.houseLetId;
    console.log(reviewId)
    if (reviewId) {
      this.reviewId = reviewId;
      this.fetchReviewForEdit(reviewId, presetHouseLetId);
    }

    // this.filelista = this.getimagesa(this.filea);
  },
  created() {
    
    // houseId: resp.houseId,houseLetId: resp.houseLetId 
        // 从路由查询参数中获取搜索关键词
        // const houseId = this.query.houseId;
        // const houseLetId = this.$route.query.houseLetId;
        // this.houseId = houseId;
        // this.houseLetId = houseLetId;

        // this.fetchHouseInfo(houseId);
        // this.fetchHouseLetInfo(houseLetId);
        // 传入搜索方法，请求houseDTO的信息，展示到前端
        // 进度条没弄出来
    },
    methods:{
      deleteImage(index) {
    const imageToRemove = this.filelista[index];
    console.log(`Deleting image: ${imageToRemove}`);

    // 从图片数组中删除
    this.filelista.splice(index, 1); // 删除文件数组中的链接
// 将剩余图片链接用逗号分隔，并更新文件字符串
    const updatedFileList = this.filelista.join(',');

    // 打印或者使用 updatedFileList 来更新界面或提交到后台
    console.log("Updated file list:", updatedFileList);

    // 如果需要更新某个字段或发送更新后的数据，可以在此操作
    this.filea = updatedFileList; // 假设你需要将其保存在 `filesUrl` 中
  },
      fetchReviewForEdit(reviewId, presetHouseLetId){
this.reviewget(reviewId,this, presetHouseLetId);
      },reviewget(reviewId,obj, presetHouseLetId){
        console.log("obj.reviewId")
        console.log(obj.reviewId)
axios.get('/api/review/getDetailedReview', {
        params: { reviewId }
      })
      .then(res => {
        const r = res.data;
        // 回填所有字段
        this.safety = r.safety;
        this.location = r.location;
        this.cleanliness = r.cleanliness;
        this.noiseLevel = r.noiseLevel;
        this.maintenance = r.maintenance;
        this.utilities = r.listingAccuracy;
        this.internet = r.internetReliability;
        this.rentValue = r.costTransparency;
        this.roomMates = r.livingComfort;
        this.roomSize = r.roomSize;
        this.stayDuration = r.stayDuration ? String(r.stayDuration) : '';
        this.roommatesNum = r.roommatesNum;
        this.monthlyRent = r.monthlyRent;
        this.reviewText = r.content;
        this.selectedButton = r.bedType || '';
        this.deposit = r.deposit;
        this.billsIncluded = r.billsIncluded !== undefined && r.billsIncluded !== null ? !!r.billsIncluded : false;
        this.roomType = r.roomType || '';
        this.biggestProblem = r.biggestProblem || '';
        this.recommendation = r.recommend !== undefined && r.recommend !== null ? !!r.recommend : null;
        this.selectedTags = (r.tags || '').split(',').filter(t => t.trim());
        this.isShared = r.isShared == 1 ? true : false;
        this.company=r.company || '';
        obj.moderationStatus = r.moderationStatus || '';
        this.ratingHouseName = (r.houseName && String(r.houseName).trim()) || '';

        this.houseId=r.houseId;
        this.houseLetId = r.houseLetId;

        if (presetHouseLetId) {
          obj.houseLetId = Number(presetHouseLetId);
        }

        this.filea = r.file


        // 回填图片（假设接口返回 URL 数组）

        console.log("fila")
        console.log(this.filea)
        this.filelista = this.getimagesa(this.filea);

        console.log(r)

        obj.fetchHouseInfo(obj, obj.houseId);
        obj.fetchHouseLetInfo(obj, obj.houseLetId);

        if (presetHouseLetId) {
          obj.$router.replace({ path: '/editReview', query: { id: String(reviewId) } });
        }
      })
      .catch(err => {
        console.error('Failed to fetch review data:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load review data. Please try again.',
          confirmButtonColor: '#3bc2ff'
        });
      });
      },
      getimagesa(filea){
      if (!filea || filea.trim() === '') return [];
      const imagesArray = filea.split(',').filter(function(s) { return s.trim() !== ''; });
      console.log("imagesArray")
        console.log(imagesArray);
        return imagesArray; // 如果不符合格式，返回原始URL
      }

      ,
         getimages(imageUrls) {
      if (!imageUrls) return [];
      // 将字符串按逗号分割为数组
      const imagesArray = imageUrls.split(',');

      // 转换为相对路径
      const relativePaths = imagesArray.map(url => {
        // 使用正则表达式提取路径部分
        const match = url.match(/http:\/\/127\.0\.0\.1:9005(\/reviewimages\/[^?]+)(\?.*)/);
        if (match) {
          // 拼接相对路径
          return match[1] + match[2];
        }
        return url; // 如果不符合格式，返回原始URL
      });

      // 输出结果
      console.log(relativePaths);
      return relativePaths;
    },
    async handleFileUpload2(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      this.isCompressingImage = true;

      const compressionOptions = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        fileType: 'image/jpeg'
      };
      const MAX_FALLBACK_SIZE = 5 * 1024 * 1024;
      const skipped = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const isHeic = /\.(heic|heif)$/i.test(file.name) || /heic|heif/i.test(file.type);
        if (isHeic) {
          skipped.push(`${file.name} — HEIC/HEIF not supported, please re-export as JPEG`);
          continue;
        }

        let toUpload = file;
        if (file.size > 1024 * 1024) {
          try {
            toUpload = await imageCompression(file, compressionOptions);
          } catch (error) {
            console.error('Image compression failed for', file.name, error);
            if (file.size > MAX_FALLBACK_SIZE) {
              skipped.push(`${file.name} — too large to upload uncompressed`);
              continue;
            }
            toUpload = file;
          }
        }

        this.fileschos.push(toUpload);
        const reader = new FileReader();
        reader.onload = (e) => {
          this.images.push(e.target.result);
        };
        reader.readAsDataURL(toUpload);
      }

      this.isCompressingImage = false;
      event.target.value = '';

      if (skipped.length) {
        Swal.fire({
          title: skipped.length === files.length ? 'Upload failed' : 'Some images skipped',
          text: skipped.join('\n'),
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#3bc2ff'
        });
      }
    },
    handleFileUpload1(event) {
      const files = event.target.files;
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const reader = new FileReader();
        reader.onload = (e) => {
          this.images.push(e.target.result);
        };
        reader.readAsDataURL(file);
      }
    },

    clearImages() {
      this.images = [];
    },
    removeImage(index) {
      this.images.splice(index, 1);
      this.fileschos = this.fileschos.filter((_, i) => i !== index);
    },
      removeImage2(index) {
      this.images.splice(index, 1);
      this.fileschos.splice(index, 1);
    },
    handleFileUpload(event) {
      this.file = event.target.files[0];
    },
preupload1() {
  if (this.roommatesNum < 0 || this.monthlyRent < 0 || this.deposit < 0) {
    let negativeField = '';
    if (this.roommatesNum < 0) negativeField = 'Number of roommates';
    else if (this.monthlyRent < 0) negativeField = 'Monthly rent';
    else if (this.deposit < 0) negativeField = 'Deposit';
    Swal.fire({
      icon: 'error',
      title: 'Invalid input',
      text: `${negativeField} input is invalid`,
      confirmButtonColor: '#3bc2ff'
    });
    this.editSaveIntent = null;
    return;
  }

  const savingDraftTap = this.isDraftModeration && this.editSaveIntent === 'draft';
  if (!savingDraftTap) {
    const zeroFields = [];
    if (this.safety === 0) zeroFields.push('safety');
    if (this.location === 0) zeroFields.push('location');
    if (this.cleanliness === 0) zeroFields.push('cleanliness');
    if (this.noiseLevel === 0) zeroFields.push('noiseLevel');
    if (this.maintenance === 0) zeroFields.push('maintenance');
    if (this.utilities === 0) zeroFields.push('utilities');
    if (this.internet === 0) zeroFields.push('internet');
    if (this.rentValue === 0) zeroFields.push('rentValue');
    if (this.roomMates === 0) zeroFields.push('roomMates');
    if (this.roomSize === 0) zeroFields.push('roomSize');
    if (zeroFields.length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'Incomplete rating',
        text: `Please rate the following: ${zeroFields.join(', ')}`,
        confirmButtonColor: '#3bc2ff'
      });
      if (this.isDraftModeration) this.editSaveIntent = null;
      return;
    }
    if (this.reviewText === '') {
      Swal.fire({
        icon: 'error',
        title: 'Missing review',
        text: 'Please write a review ~',
        confirmButtonColor: '#3bc2ff'
      });
      if (this.isDraftModeration) this.editSaveIntent = null;
      return;
    }
  }

  console.log('filesURL');
  console.log(this.fileschos);
  const hasNewImages = Array.isArray(this.fileschos) && this.fileschos.length > 0;
  if (!hasNewImages) {
    this.rateHouse(this);
  } else {
    this.uploadFileNew(this);
  }
}
    
    ,
      preupload(){


        // if (this.checkForZeroFields()) {
        //         i
        //   }else{
        //     console.log('Form has fields with zero values. Please fill them out.');
        //   }

      
      },

      uploadFileNew(obj) {

      console.log(obj.fileschos)
        console.log(typeof obj.fileschos); // 输出类型
        console.log(typeof obj.fileschos[0]); // 输出类型
      // 没能append到'file'中
      // 创建 FormData 对象
      const formData = new FormData();

      // 遍历文件数组，将文件添加到 FormData 中
      this.fileschos.forEach((file) => {
        console.log(file)
        formData.append(`files`, file); // 可以为每个文件设置不同的 key，例如 file0, file1...
      });

      const formDataArray = Array.from(formData.entries());
      console.log(formDataArray);

      const expectedFiles = this.fileschos.length;

      axios.post('/api/upload/reviewimg', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000
      })
      .then(response => {
        console.log(response);
        let payload = response && response.data;
        if (typeof payload === 'string') {
          try { payload = JSON.parse(payload); } catch (e) { payload = null; }
        }
        const code = payload && payload.code;
        const data = payload && payload.data;
        if (code !== undefined && Number(code) !== 1) {
          obj.editSaveIntent = null;
          Swal.fire({
            icon: 'error',
            title: 'Upload failed',
            text: (payload && payload.msg) || 'Images were not saved.',
            confirmButtonColor: '#3bc2ff'
          });
          return;
        }
        const urlStr = data != null ? String(data).trim() : '';
        if (!urlStr || urlStr.toLowerCase() === 'null') {
          obj.editSaveIntent = null;
          Swal.fire({
            icon: 'error',
            title: 'Upload failed',
            text: 'No image URLs returned. Check MinIO or try again.',
            confirmButtonColor: '#3bc2ff'
          });
          return;
        }
        const urlParts = urlStr.split(',').map((s) => s.trim()).filter(Boolean);
        if (urlParts.length < expectedFiles) {
          Swal.fire({
            icon: 'warning',
            title: 'Some images may be missing',
            html: `Only <strong>${urlParts.length}</strong> of <strong>${expectedFiles}</strong> images were saved. Continue with this review save or cancel and retry uploads.`,
            showCancelButton: true,
            confirmButtonText: 'Continue & save review',
            cancelButtonText: 'Go back',
            confirmButtonColor: '#3bc2ff',
            cancelButtonColor: '#64748b'
          }).then((r) => {
            if (!r.isConfirmed) {
              obj.editSaveIntent = null;
              return;
            }
            obj.filesUrl = urlStr;
            console.log(obj.filesUrl);
            obj.rateHouse(obj);
          });
          return;
        }
        obj.filesUrl = urlStr;
        console.log(obj.filesUrl);
        obj.rateHouse(obj);
      })
      .catch(error => {
        obj.editSaveIntent = null;
        const msg = error?.response?.data?.msg || error?.message || 'Upload failed';
        Swal.fire({
          icon: 'error',
          title: 'Upload failed',
          text: msg,
          confirmButtonColor: '#3bc2ff'
        });
      });
    },
        checkForZeroFields() {
      const zeroFields = [];
      const fields = {
        safety: this.safety,
        location: this.location,
        cleanliness: this.cleanliness,
        noiseLevel: this.noiseLevel,
        maintenance: this.maintenance,
        utilities: this.utilities,
        internet: this.internet,
        rentValue: this.rentValue,
        roommates: this.roomMates,
        roomSize: this.roomSize,
      };

      for (const key in fields) {
        if (fields[key] === 0) {
          zeroFields.push(key);
        }
      }

      if (zeroFields.length > 0) {
        Swal.fire({
          title: 'Alert',
          text: `Please provide a rating for the following fields: ${zeroFields.join(', ')}`,
          icon: 'info',
          confirmButtonText: 'OK',
          confirmButtonColor: '#3bc2ff'
        });
        return false;
      }
      return true;
    },
    uploadFile(obj) {


      console.log(obj.fileschos)
      // 没能append到'file'中
      // const formData = new FormData();
      // formData.append('file', obj.fileschos);
      const filesData = Array.from(this.fileschos).map(fileList => {
          return Array.from(fileList).map(file => ({
            name: file.name,
            lastModified: file.lastModified,
            lastModifiedDate: file.lastModifiedDate.toUTCString(),
            webkitRelativePath: file.webkitRelativePath,
            size: file.size
          }));
        });

        // 转换为 JSON 字符串
        const jsonData = JSON.stringify(filesData);
        console.log(jsonData);
      axios.post('/api/upload/reviewimg', jsonData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then(response => {
        console.log(response)
        alert('文件上传成功！保存路径：' + response.data.data);
        obj.filesUrl=response.data.data
        console.log(obj.filesUrl)
      })
      .catch(error => {
        alert('文件上传失败：' +error);
      });
    },

      uploadFiles() {
      const formData = new FormData();
      for (let i = 0; i < this.fileschos.length; i++) {
        formData.append('files', this.fileschos[i]); // 将每个文件追加到 FormData 中
      }

      console.log(formData)
      axios.post('/api/upload/reviewimg', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then(response => {
        alert('文件上传成功！保存路径：' + response.data); // 假设服务器返回的是保存路径
      })
      .catch(error => {
        alert('文件上传失败：' + error);
      });
    },
    // readFile() {
    //   if (!this.fileName) {
    //     alert('请输入文件名！');
    //     return;
    //   }
    //
    //   axios.get(`http://localhost:8088/upload/readFile?fileName=${this.fileName}`)
    //   .then(response => {
    //     this.fileContent = response.data;
    //   })
    //   .catch(error => {
    //     alert('读取文件失败：' + error.response.data);
    //   });
    // },

      fetchHouseInfo(houseId){
      this.getHouseInfo(this,houseId)
  },
  fetchHouseLetInfo(houseletId){
      this.getHouseLetInfo(this,houseletId)
  },

      getHouseInfo(obj,house){
        // 在这里实现跳转到房屋评分页面的逻辑

        axios.get('/api/house', {
          params: {
              houseId: house,
          }
      })
      .then(function (response) {
          obj.house = response.data.data != null ? response.data.data : {};
          console.log(response.data.data);
      })
      .catch(function (error) {
          console.log(error);
          obj.house = {};
      });
  },
  getHouseLetInfo(obj,houseletId){
        // 在这里实现跳转到房屋评分页面的逻辑

        axios.get('/api/house/let', {
          params: {
            houseletId: houseletId,
          }
      })
      .then(function (response) {
          obj.houselet = response.data.data != null ? response.data.data : {};
          console.log(response.data.data);
      })
      .catch(function (error) {
          console.log(error);
          obj.houselet = {};
      });
  },
      showGuidelines() {
      // 显示指南的逻辑
    },
    onFileChange(event) {
      // 处理文件上传的逻辑
      console.log(event.target.files[0]);
    },
    publishDraftReview() {
      this.editSaveIntent = 'publish';
      this.submitReview();
    },
    saveDraftEdits() {
      this.editSaveIntent = 'draft';
      this.preupload1();
    },
    submitReview() {
      // Enhanced validation with detailed error messages
      const errors = [];
      
      // Check rating fields
      const ratingFields = [
        { name: 'Safety', value: this.safety },
        { name: 'Location Convenience', value: this.location },
        { name: 'Cleanliness & Condition', value: this.cleanliness },
        { name: 'Noise Exposure', value: this.noiseLevel },
        { name: 'Maintenance Responsiveness', value: this.maintenance },
        { name: 'Listing Accuracy', value: this.utilities },
        { name: 'Internet & Utilities Reliability', value: this.internet },
        { name: 'Cost Transparency', value: this.rentValue },
        { name: 'Space Adequacy', value: this.roomSize },
        { name: 'Living Comfort', value: this.roomMates }
      ];
      
      ratingFields.forEach(field => {
        if (field.value === 0) {
          errors.push(`${field.name} rating is required`);
        }
      });
      
      // Check review text
      if (!this.reviewText || this.reviewText.trim().length < 10) {
        errors.push('Review text must be at least 10 characters long');
      }
      
      // Check basic information fields
      if (!this.roomType) {
        errors.push('Please select your room type');
      }
      if (!this.stayDuration) {
        errors.push('Please select how long you stayed');
      }
      if (this.monthlyRent === null || this.monthlyRent === undefined || this.monthlyRent === '') {
        errors.push('Please enter the monthly rent');
      }
      if (this.roommatesNum === null || this.roommatesNum === undefined || this.roommatesNum === '' || this.roommatesNum < 0) {
        errors.push('Please enter the number of roommates (must be 0 or greater)');
      }
      if (!this.biggestProblem || this.biggestProblem.trim().length === 0) {
        errors.push('Please describe the biggest problem you faced');
      }
      if (this.recommendation === null || this.recommendation === undefined) {
        errors.push('Please indicate whether you would recommend this property');
      }
      
      // Show validation errors if any
      if (errors.length > 0) {
        if (this.isDraftModeration) {
          this.editSaveIntent = null;
        }
        Swal.fire({
          icon: 'warning',
          title: '<span style="color: #2c3e50; font-size: 1.5rem; font-weight: 600;">Please Complete Required Fields</span>',
          html: `
            <div style="text-align: left; margin: 20px 0;">
              <div style="background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 8px; padding: 15px; margin-bottom: 15px;">
                <div style="color: #856404; font-weight: 600; margin-bottom: 10px;">Required Information:</div>
                <div style="color: #856404;">
                  ${errors.map(error => `<div style="margin: 8px 0; padding-left: 20px; position: relative;">
                    <span style="position: absolute; left: 0; color: #e74c3c;">•</span>
                    ${error}
                  </div>`).join('')}
                </div>
              </div>
            </div>
          `,
          confirmButtonText: 'Got it!',
          confirmButtonColor: '#3bc2ff',
          cancelButtonColor: '#64748b',
          background: '#ffffff',
          showCancelButton: true,
          cancelButtonText: 'Close',
          allowOutsideClick: true,
          allowEscapeKey: true,
        });
        return;
      }
      
      // If validation passes, proceed with submission
      this.preupload1();
    },
    toggleTag(tag) {
      const idx = this.selectedTags.indexOf(tag);
      if (idx === -1) {
        this.selectedTags.push(tag);
      } else {
        this.selectedTags.splice(idx, 1);
      }
    },
      selectButton(index) {
      // Toggle: clicking same button deselects, clicking different selects
      if (this.selectedButton === index) {
        this.selectedButton = null;
      } else {
        this.selectedButton = index;
      }
    },
        ToHouseReviewScore() {

          if (this.$route.path !== '/HouseReviewScore') {
            this.$router.push({ path: '/HouseReviewScore', query: { houseId: this.houseId,houseLetId: this.houseLetId } });
          }

        },
        ToAddLocation(){
          if (this.$route.path !== '/AddLocation') {
            this.$router.push({ path: '/AddLocation', query: { houseId: this.houseId,houseLetId: this.houseLetId } });
          }
        },
        goAddOrSwitchRoom() {
          if (!this.houseId) {
            Swal.fire({
              icon: 'info',
              title: 'Still loading',
              text: 'Please wait until your review finishes loading.',
              confirmButtonColor: '#3bc2ff'
            });
            return;
          }
          this.$router.push({
            path: '/AddHouseRoom',
            query: {
              houseId: String(this.houseId),
              from: 'editReview',
              reviewId: String(this.reviewId)
            }
          });
        },
    rateHouse(obj) {
        let files = '';
        if (this.filea !== '' && this.filesUrl !== '') {
          files = this.filea + ',' + this.filesUrl;
        } else if (this.filea !== '') {
          files = this.filea;
        } else {
          files = this.filesUrl;
        }

        const wasSavingDraft = this.editSaveIntent === 'draft';
        const publishFromDraft = this.isDraftModeration && this.editSaveIntent === 'publish';

        console.log(publishFromDraft ? 'Publishing draft…' : 'Saving review edits…');

        const payload = {
          reviewId: this.reviewId,
          houseId: this.houseId,
          content: this.reviewText,
          safety: this.safety,
          location: this.location,
          cleanliness: this.cleanliness,
          noiseLevel: this.noiseLevel,
          maintenance: this.maintenance,
          listingAccuracy: this.utilities,
          internetReliability: this.internet,
          costTransparency: this.rentValue,
          livingComfort: this.roomMates,
          roomSize: this.roomSize,
          houseLetId: this.houseLetId,
          stayDuration: parseInt(this.stayDuration, 10) || 0,
          roommatesNum: this.roommatesNum,
          deposit: parseInt(this.deposit, 10) || 0,
          sharedBathroom: this.isShared ? 1 : 0,
          monthlyRent: parseInt(this.monthlyRent, 10) || 0,
          bedType: this.selectedButton,
          file: files,
          recommend: this.recommendation,
          company: this.company || '',
          biggestProblem: this.biggestProblem || '',
          tags: this.selectedTags.join(','),
          billsIncluded: this.billsIncluded,
          roomType: this.roomType || '',
          isShared: this.isShared ? 1 : 0,
          ifWantVerify: 0
        };

        if (publishFromDraft) {
          payload.publishReview = true;
        }

        axios.post('/api/review/editReview', payload)
        .then(function (response) {
            const bodyResp = response.data || {};
            if (bodyResp.code !== undefined && bodyResp.code !== 1) {
              obj.editSaveIntent = null;
              Swal.fire({
                icon: 'error',
                title: 'Could not save',
                text: bodyResp.msg || 'Your review could not be updated.',
                confirmButtonColor: '#3bc2ff'
              });
              return;
            }

            obj.editSaveIntent = null;

            if (wasSavingDraft) {
              Swal.fire({
                title: 'Draft saved',
                text: 'Your edits were saved. You can publish whenever you’re ready.',
                icon: 'success',
                confirmButtonText: 'OK',
                confirmButtonColor: '#3bc2ff'
              });
              return;
            }

            Swal.fire({
                title: 'Success!',
                text: 'Your review has been updated successfully.',
                icon: 'success',
                confirmButtonText: 'OK',
                confirmButtonColor: '#3bc2ff'
              }).then(function() {
                obj.ToHouseReviewScore();
              });
        })
        .catch(function (error) {
          obj.editSaveIntent = null;
          console.log(error);
          const msg = error.response && error.response.data && (error.response.data.msg || error.response.data.message);
          Swal.fire({
            icon: 'error',
            title: 'Save failed',
            text: msg || 'Could not update your review. Please try again.',
            confirmButtonColor: '#3bc2ff'
          });
        });
    
      },
        //       ToHouseReviewScore() {

        //   if (this.$route.path !== '/HouseReviewScore') {
        //     this.$router.push({ path: '/HouseReviewScore', query: { houseId: this.houseId,houseLetId: this.houseLetId } });
        //   }

        // },
    }
}

    
  </script>

<style scoped>
.outer_box {
  background: linear-gradient(135deg, #e6f0ff 0%, #f8fbff 100%);
  min-height: 100vh;
  padding: 0 0 48px 0;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}
.all_out_box {
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 8px 32px 0 rgba(74,144,226,0.10), 0 2px 8px rgba(74,144,226,0.08);
  padding: 24px 32px 32px 32px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
.tttiltleBox {
  margin-bottom: 32px;
}
.title-container {
  text-align: center;
}
.main-title {
  font-size: 2.2rem;
  font-weight: 700;
  color: #357ab8;
  margin-bottom: 8px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  padding: 0 16px;
}
.main-title-edit {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: center;
  gap: 0.35rem 0.45rem;
}
.main-title-edit .house-name-text {
  word-break: break-word;
  white-space: normal;
}
.main-title-edit .title-room-sep {
  opacity: 0.55;
  font-weight: 600;
  color: #94a3b8;
}
.main-title-edit .house-room-text {
  color: #1d4ed8;
  font-weight: 700;
}
.title-underline {
  width: 60px;
  height: 4px;
  background: linear-gradient(90deg, #4a90e2 60%, #6eb1f7 100%);
  border-radius: 2px;
  margin: 0 auto 8px auto;
}
.subtitle {
  color: #7b8ca6;
  font-size: 1.1rem;
  margin-bottom: 0;
}
.top_rate {
  margin-bottom: 32px;
}
.top_rate_right {
  background: #f8fbff;
  border-radius: 18px;
  box-shadow: 0 2px 12px rgba(74,144,226,0.10);
  padding: 28px 32px 24px 32px;
}
.form-section {
  margin-bottom: 24px;
}
.section-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #357ab8;
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.section-icon {
  font-size: 1.2rem;
}
.input-group {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-bottom: 18px;
}
.input-field {
  flex: 1 1 180px;
  display: flex;
  flex-direction: column;
  position: relative;
}
.form-label {
  font-weight: 500;
  color: #357ab8;
  margin-bottom: 6px;
}
.input-container {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 8px;
  border: 1.5px solid #b3d1f7;
  padding: 0 10px;
}
.custom-input.modern-input {
  border: none;
  outline: none;
  background: transparent;
  padding: 10px 0;
  font-size: 1rem;
  color: #222;
  flex: 1;
}
.input-suffix {
  color: #7b8ca6;
  font-size: 1rem;
  margin-left: 6px;
}
.button-group {
  margin-top: 18px;
}
.button-container {
  display: flex;
  gap: 12px;
}
.bed-type-button.modern-btn {
  background: #f8fbff;
  border: 1.5px solid #b3d1f7;
  border-radius: 999px;
  color: #357ab8;
  font-weight: 500;
  font-size: 1rem;
  padding: 8px 24px;
  cursor: pointer;
  transition: background 0.2s, border 0.2s, color 0.2s;
}
.bed-type-button.modern-btn.active {
  background: linear-gradient(90deg, #4a90e2 60%, #6eb1f7 100%);
  color: #fff;
  border-color: #4a90e2;
  box-shadow: 0 2px 8px rgba(74,144,226,0.10);
}
.bed-type-button.modern-btn:hover, .bed-type-button.modern-btn:focus {
  background: #e6f0ff;
  border-color: #4a90e2;
}
.tmp_rate {
  margin-bottom: 32px;
}
.rateBlock {
  background: #f8fbff;
  border-radius: 18px;
  box-shadow: 0 2px 12px rgba(74,144,226,0.10);
  padding: 28px 32px 24px 32px;
}
.rating-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 24px;
  margin-bottom: 24px;
}
.rating-item {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 1px 6px rgba(74,144,226,0.06);
  padding: 18px 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  transition: box-shadow 0.2s, background 0.2s;
}
.rating-item:hover {
  background: #e6f0ff;
  box-shadow: 0 4px 16px rgba(74,144,226,0.10);
}
.rating-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}
.icon-container {
  background: #e6f0ff;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.rating-label {
  font-weight: 600;
  color: #357ab8;
  font-size: 1.08rem;
}
.rating-hint {
  font-size: 0.78rem;
  color: #94a3b8;
  margin-bottom: 2px;
  font-style: italic;
}
.el_rate_yes {
  margin-top: 6px;
}
.reviewBlock {
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 2px 12px rgba(74,144,226,0.10);
  padding: 32px 28px;
  margin-top: 16px;
}
.review-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #357ab8;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.review-description {
  color: #7b8ca6;
  font-size: 1.08rem;
  margin-bottom: 18px;
}
.upload-section {
  margin-bottom: 18px;
}
.upload-title {
  font-size: 1.08rem;
  color: #357ab8;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.upload-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.file-input-wrapper {
  position: relative;
  width: 180px;
  height: 48px;
}

.file-input {
  opacity: 0;
  width: 100%;
  height: 100%;
  position: absolute;  /* 确保 input 元素覆盖整个容器 */
  left: 0;
  top: 0;
  cursor: pointer;
  z-index: 2;  /* 确保它处于最上层 */
}

.file-input-overlay {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: #e6f0ff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #357ab8;
  font-weight: 500;
  font-size: 1.08rem;
  cursor: pointer;
  transition: background 0.2s;
  z-index: 1;  /* 确保它不会遮挡 input 元素 */
}

.file-input-overlay:hover {
  background: #b3d1f7;
}

.image-gallery {
  display: flex;
  gap: 10px;
  margin-top: 8px;
}
.image-preview {
  position: relative;
  width: 80px;
  height: 80px;
}
.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 10px;
  box-shadow: 0 1px 6px rgba(74,144,226,0.10);
}
.delete-button {
  position: absolute;
  top: 2px;
  right: 2px;
  background: #fff;
  border: 1.5px solid #ff4757;
  color: #ff4757;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background 0.2s, border 0.2s;
}
.delete-button:hover {
  background: #ff4757;
  color: #fff;
}
.review-textarea-container {
  margin-top: 18px;
  position: relative;
}
.review-textarea {
  width: 100%;
  min-height: 90px;
  border-radius: 10px;
  border: 1.5px solid #b3d1f7;
  background: #f8fbff;
  padding: 12px;
  font-size: 1.08rem;
  color: #222;
  resize: vertical;
  transition: border 0.2s;
}
.review-textarea:focus {
  border-color: #4a90e2;
  outline: none;
}
.character-count {
  position: absolute;
  bottom: 8px;
  right: 16px;
  color: #7b8ca6;
  font-size: 0.98rem;
}
.submit-button {
  margin-top: 24px;
  width: 100%;
  padding: 16px 0;
  border: none;
  border-radius: 10px;
  background: linear-gradient(90deg, #4a90e2 60%, #6eb1f7 100%);
  color: #fff;
  font-weight: 700;
  font-size: 1.15rem;
  box-shadow: 0 2px 8px rgba(74,144,226,0.10);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: background 0.2s, box-shadow 0.2s, transform 0.2s;
}
.submit-button:hover, .submit-button:focus {
  background: linear-gradient(90deg, #357ab8 60%, #4a90e2 100%);
  box-shadow: 0 4px 16px rgba(74,144,226,0.14);
  transform: translateY(-2px) scale(1.04);
}
.button-text {
  font-size: 1.15rem;
}
.button-icon {
  font-size: 1.3rem;
}
.agreement-text {
  color: #7b8ca6;
  font-size: 0.98rem;
  margin-top: 18px;
  text-align: center;
}
.agreement-link {
  color: #4a90e2;
  text-decoration: underline;
  transition: color 0.2s;
}
.agreement-link:hover {
  color: #357ab8;
}
@media (max-width: 900px) {
  .all_out_box {
    padding: 24px 8px 18px 8px;
    max-width: 99vw;
  }
}
@media (max-width: 700px) {
  .all_out_box {
    padding: 10px 2vw 8px 2vw;
    max-width: 99vw;
  }
  .top_rate_right, .rateBlock, .reviewBlock {
    padding: 14px 8px;
  }
  .main-title {
    font-size: 1.2rem;
  }
}

/* New form element styles for Task 6 */
.select-container {
  position: relative;
}
.select-container select.modern-select {
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: none;
  background: #e7e7e7;
  font-size: 1rem;
  color: #222;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  box-shadow: inset 2px 2px 5px rgba(0,0,0,0.1),
              inset -2px -2px 5px rgba(255,255,255,0.9);
  outline: none;
}
.select-container select.modern-select:focus {
  box-shadow: inset 4px 4px 8px rgba(0,0,0,0.15),
              inset -4px -4px 8px rgba(255,255,255,0.9);
}
.select-container::after {
  content: '▾';
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #7b8ca6;
  pointer-events: none;
  font-size: 1rem;
}
.input-prefix {
  color: #7b8ca6;
  font-weight: 600;
  font-size: 0.95rem;
  margin-right: 4px;
  white-space: nowrap;
}
.custom-input.modern-input.with-prefix {
  padding-left: 6px;
}
.toggle-group {
  display: flex;
  gap: 8px;
}
.toggle-btn {
  flex: 1;
  padding: 10px 18px;
  border-radius: 999px;
  border: none;
  background: #e7e7e7;
  color: #357ab8;
  font-weight: 500;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
  box-shadow: inset 2px 2px 5px rgba(0,0,0,0.1),
              inset -2px -2px 5px rgba(255,255,255,0.9);
}
.toggle-btn.active {
  background: #e7e7e7;
  color: #4a90e2;
  box-shadow: inset 4px 4px 8px rgba(0,0,0,0.15),
              inset -4px -4px 8px rgba(255,255,255,0.9);
}
.toggle-btn:hover {
  color: #4a90e2;
}
.insight-section {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}
.tag-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}
.tag-chip {
  padding: 6px 16px;
  border-radius: 999px;
  border: 1.5px solid #cbd5e1;
  background: #fff;
  color: #475569;
  font-size: 0.88rem;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
}
.tag-chip.active {
  background: #e0f2fe;
  border-color: #4a90e2;
  color: #1e40af;
}
.tag-chip:hover {
  border-color: #4a90e2;
  color: #4a90e2;
}
</style>
